// Spotlight Promotions — the paid card at the front of the Home rail.
//
// Called "Gnosis Featured" everywhere a user can read it; "promotion" is the
// record's name in here and in Firestore, and the two are not worth churning
// into alignment. Featured names what the buyer gets, which keeps it clear of
// "Gnosis Pro" — the thing members already have.
//
// Buying is a submission, not a purchase: nothing is charged until the admin
// approves, and approval is what swaps the member's subscription price to the
// Promotion price. So there is no checkout redirect anywhere in here.
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/** What a Promotion costs, in dollars. Membership is included at this price —
 *  the Promotion price replaces the membership one rather than adding to it. */
export const PROMO_PRICE = 25;

/** Where a sponsor's handle lives. Not everyone teaching is on Instagram, so
 *  the handle has to say which site it belongs to — but Instagram stays the
 *  default: it is where the catalog comes from, and the only one the portraits
 *  script can fetch a face from. */
export type PromoPlatform = 'instagram' | 'tiktok' | 'youtube';

export const PROMO_PLATFORMS: { id: PromoPlatform; label: string }[] = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'youtube', label: 'YouTube' },
];

/** Their profile, wherever it is. A record written before platforms existed
 *  has none, and every one of those is Instagram. */
export function profileUrl(handle: string, platform?: PromoPlatform | null): string {
  if (platform === 'tiktok') return `https://tiktok.com/@${handle}`;
  if (platform === 'youtube') return `https://youtube.com/@${handle}`;
  return `https://instagram.com/${handle}`;
}

/** What the buyer fills in. The link is optional: a Promotion without one is
 *  still a card at the front of the rail, it just lands on their Gnosis page
 *  rather than sending anyone out. */
export interface PromotionFields {
  /** Their handle, no @. An Instagram one also feeds the portraits script
   *  that fetches the face; the other platforms need one dropped in by hand. */
  handle: string;
  platform?: PromoPlatform;
  name: string;
  blurb: string;
  url?: string;
}

export type PromotionStatus = 'pending' | 'live' | 'paused' | 'rejected';

/** A Promotion as it sits on the owner's own user doc. */
export interface Promotion extends PromotionFields {
  status: PromotionStatus;
  /** An edit waiting for review. The live card keeps showing the approved
   *  version until this is approved, so unreviewed text never reaches Home. */
  pending?: PromotionFields | null;
  rejectReason?: string | null;
  ltvCents?: number;
  clicks?: number;
}

/** A live Promotion as everyone else's app reads it, out of stats/promotions. */
export interface LivePromotion {
  uid: string;
  handle: string;
  platform: PromoPlatform | null;
  name: string;
  url: string | null;
  blurb: string | null;
  ltvCents: number;
  clicks: number;
}

/** Submit a new Promotion, or stage an edit to one already running. Either
 *  way it lands in the admin queue and nothing is charged. */
export async function submitPromotion(fields: PromotionFields): Promise<{ status: PromotionStatus; staged: boolean; needsCard: boolean }> {
  const fn = httpsCallable<PromotionFields, { status: PromotionStatus; staged: boolean; needsCard: boolean }>(functions, 'submitPromotion');
  return (await fn(fields)).data;
}

/** Somewhere to save a card for a buyer who has no subscription — a gift-code
 *  user. Stripe's setup mode stores the card without charging it, so approval
 *  is still the only thing that takes money. Returns the URL to send them to. */
export async function createPromotionSetupSession(): Promise<string> {
  const fn = httpsCallable<{ origin: string }, { url: string }>(functions, 'createPromotionSetupSession');
  return (await fn({ origin: window.location.origin })).data.url;
}

/** Cancel from Settings. Someone holding frozen days gets those back instead
 *  of dropping to the membership price — they should not pay while sitting on
 *  credit they already own. */
export async function cancelPromotion(): Promise<{ cancelled: boolean; restoredDays?: number }> {
  const fn = httpsCallable<Record<string, never>, { cancelled: boolean; restoredDays?: number }>(functions, 'cancelPromotion');
  return (await fn({})).data;
}

/** One outbound tap. Counted for the admin only — never shown to the sponsor.
 *  Best-effort: a failure here must never block opening the link. */
export function recordPromotionClick(uid: string): void {
  httpsCallable(functions, 'recordPromotionClick')({ uid }).catch(() => {});
}

/** One row of the admin review queue. `kind` separates a first submission
 *  from an edit to a Promotion that is already live. */
export interface PendingPromotion extends Promotion {
  uid: string;
  email: string | null;
  kind: 'new' | 'edit';
  createdAt: number | null;
}

// Admin only.
export async function listPendingPromotions(): Promise<PendingPromotion[]> {
  const fn = httpsCallable<Record<string, never>, { pending: PendingPromotion[] }>(functions, 'listPendingPromotions');
  return (await fn({})).data.pending;
}

// Admin only. Approving a first submission is the moment billing starts.
export async function reviewPromotion(uid: string, approve: boolean, reason?: string): Promise<{ status: PromotionStatus }> {
  const fn = httpsCallable<{ uid: string; approve: boolean; reason?: string }, { status: PromotionStatus }>(functions, 'reviewPromotion');
  return (await fn({ uid, approve, reason })).data;
}
